import {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from "react";
import { ProductImage } from "../ProductImage";
import {
  getMouseChangeKeyLayout,
  type MouseChangeKeySpotId,
} from "@eweadn-qiankun/shared-utils";
import "../../../../mouse-management/src/pages/changeKey/Mouse.scss";

export interface CommunityMouseSpotLabel {
  title: string;
  subtext?: string;
}

export interface CommunityMouseVisualizerProps {
  displayname: string;
  deviceId: string;
  spotIds?: MouseChangeKeySpotId[];
  spotLabels: Partial<Record<MouseChangeKeySpotId, CommunityMouseSpotLabel>>;
  selectedSpotId?: MouseChangeKeySpotId | null;
  className?: string;
}

/** 社区预览：复用改键页鼠标可视化布局（无设备 SDK 依赖�?*/
export default function CommunityMouseVisualizer({
  displayname,
  deviceId,
  spotIds,
  spotLabels,
  selectedSpotId = null,
  className = "",
}: CommunityMouseVisualizerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hotspotsRef = useRef<Record<string, HTMLDivElement | null>>({});
  const labelsRef = useRef<Record<string, HTMLDivElement | null>>({});
  const [lines, setLines] = useState<
    Record<
      string,
      { x1: number; y1: number; x2: number; y2: number; path?: string }
    >
  >({});
  const [imageLoaded, setImageLoaded] = useState(false);

  const buttons = useMemo(() => {
    const spots = getMouseChangeKeyLayout(deviceId).filter((spot) =>
      spotIds ? spotIds.includes(spot.id) : true
    );
    return spots.map((spot) => ({
      ...spot,
      label: spotLabels[spot.id]?.title ?? spot.id,
      subtext: spotLabels[spot.id]?.subtext,
    }));
  }, [deviceId, spotIds, spotLabels]);

  const updateLines = useCallback(() => {
    if (!containerRef.current || !imageLoaded) return;

    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;
      const newLines: typeof lines = {};
      const TOP_BUTTON_IDS = new Set(["left", "middle", "right"]);
      const TOP_STEM_IDS = new Set(["left", "right"]);
      const RIGHT_STEM_LENGTH_BY_ID: Record<string, number> = {
        scrollUp: (72 / 264) * containerWidth,
        scrollDown: (72 / 264) * containerWidth,
        dpi: (32 / 264) * containerWidth,
      };
      const TOP_STEM_LENGTH = (32 / 474) * containerHeight;

      buttons.forEach((button) => {
        const hotspot = hotspotsRef.current[button.id];
        const label = labelsRef.current[button.id];
        const hotspotX = (button.position.x / 100) * containerWidth;
        const hotspotY = (button.position.y / 100) * containerHeight;
        const hotspotRadius = hotspot ? hotspot.offsetWidth / 2 : 14;
        const labelAnchorX = (button.labelPosition.x / 100) * containerWidth;
        const labelAnchorY = (button.labelPosition.y / 100) * containerHeight;
        const isTopButton = TOP_BUTTON_IDS.has(button.id);
        const hasTopStem = TOP_STEM_IDS.has(button.id);
        const rightStemLength = RIGHT_STEM_LENGTH_BY_ID[button.id];
        const hasRightStem = rightStemLength != null;
        const labelBottomOffset = label ? label.offsetHeight / 2 : 24;
        const labelHalfWidth = label ? label.offsetWidth / 2 : 48;
        const x2 = hasRightStem ? labelAnchorX - labelHalfWidth : labelAnchorX;
        const y2 = isTopButton
          ? labelAnchorY + labelBottomOffset
          : labelAnchorY;
        const elbowX = hasRightStem ? x2 - rightStemLength : x2;
        const elbowY = hasTopStem ? y2 + TOP_STEM_LENGTH : y2;
        const dx = elbowX - hotspotX;
        const dy = elbowY - hotspotY;
        const distance = Math.hypot(dx, dy) || 1;
        const x1 = hotspotX + (dx / distance) * hotspotRadius;
        const y1 = hotspotY + (dy / distance) * hotspotRadius;
        let path: string;
        if (hasTopStem) {
          path = `M ${x2} ${y2} L ${x2} ${elbowY} L ${x1} ${y1}`;
        } else if (hasRightStem) {
          path = `M ${x2} ${y2} L ${elbowX} ${y2} L ${x1} ${y1}`;
        } else {
          path = `M ${x2} ${y2} L ${x1} ${y1}`;
        }
        newLines[button.id] = { x1, y1, x2, y2, path };
      });
      setLines(newLines);
    });
  }, [buttons, imageLoaded]);

  useEffect(() => {
    if (!imageLoaded) return;
    const timer = setTimeout(updateLines, 50);
    return () => clearTimeout(timer);
  }, [imageLoaded, updateLines]);

  useEffect(() => {
    if (imageLoaded) updateLines();
  }, [selectedSpotId, updateLines, imageLoaded]);

  useEffect(() => {
    if (!imageLoaded || !containerRef.current) return;
    const observer = new ResizeObserver(updateLines);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [imageLoaded, updateLines]);

  const renderLabel = (button: (typeof buttons)[number]): ReactNode => (
    <div
      ref={(el) => {
        labelsRef.current[button.id] = el;
      }}
      className={`mouse-label ${button.align === "right" ? "align-right" : ""}`}
      style={{
        left: `${button.labelPosition.x}%`,
        top: `${button.labelPosition.y}%`,
      }}
    >
      <div className="label-content">
        <div className="label-title">{button.label}</div>
        {button.subtext ? (
          <div className="label-subtext">{button.subtext}</div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={`mouse-container ${className}`.trim()}>
      <div className="mouse-visualizer" ref={containerRef}>
        <svg className="connection-lines-container">
          {buttons.map((button) => {
            const line = lines[button.id];
            if (!line) return null;
            if (line.path) {
              return <path key={`line-${button.id}`} d={line.path} />;
            }
            return (
              <line
                key={`line-${button.id}`}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
              />
            );
          })}
        </svg>

        <ProductImage
          displayname={displayname}
          alt={displayname}
          className="mouse-image"
          onLoad={() => setImageLoaded(true)}
        />

        {buttons.map((button) => (
          <div key={button.id}>
            <div
              ref={(el) => {
                hotspotsRef.current[button.id] = el;
              }}
              className={`mouse-hotspot${
                selectedSpotId === button.id ? " selected" : ""
              }`}
              style={{
                left: `${button.position.x}%`,
                top: `${button.position.y}%`,
              }}
            />
            {renderLabel(button)}
          </div>
        ))}
      </div>
    </div>
  );
}
